import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
} from "@mui/material";
import { Provider } from "react-redux";
import { store } from "@/app/store";
import { VideoPreviewPanel } from "@/pages/Board/bugherdComponents/VideoPreviewPanel";
import { filterVideoComments } from "@/utils/videoUtils";
import { useGetBugDetailByBugIdQuery, useGetPublicCommentsByBugIdQuery, useCreatePublicCommentMutation } from "@/features/bughurd/api";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import PhantomLoader from "@/Components/Common/phantomLoader";

interface SystemInfo {
  url?: string;
  timestamp?: string | Date;
  os?: string;
  browser?: string;
  windowSize?: string;
  screenshotDimensions?: string;
}

interface DeviceDetails {
  operatingSystem?: string;
  browser?: string;
  screen?: string;
  hardware?: {
    cpuCores?: number;
    memory?: string;
    platform?: string;
    architecture?: string;
    cookieEnabled?: boolean;
    colorDepth?: number;
  };
  language?: {
    primary?: string;
    timeZone?: string;
  };
  url?: string;
}

const S3_BASE_URL = import.meta.env.VITE_S3_BASE_URL || "";

const PublicBugDetailContent: React.FC = () => {
  const { bugId } = useParams<{ bugId: string }>();

  // State for fetched logs and video URL
  const [consoleLogs, setConsoleLogs] = useState<any[]>([]);
  const [networkLogs, setNetworkLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);

  // API hooks
  const [postAttachmentKey] = useGetAttachmentUrlMutation();

  const {
    data: bugDetailResponse,
    isLoading: isBugLoading,
    error: bugError,
  } = useGetBugDetailByBugIdQuery(bugId || "", { skip: !bugId });

  const {
    data: commentsResponse,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
  } = useGetPublicCommentsByBugIdQuery(bugId || "", { skip: !bugId });

  const [createPublicComment, { isLoading: isCreatingComment }] = useCreatePublicCommentMutation();

  // Extract bug data
  const bugData = bugDetailResponse?.data?.decryptedData || bugDetailResponse?.data || null;

  // Fetch video URL from videoRecordings array (similar to TaskUpdateDrawer)
  useEffect(() => {
    const fetchVideoUrl = async () => {
      const rawUrl = bugData?.videoRecordings?.[0]?.url;
      if (!rawUrl) {
        // Fallback to other possible video URL locations
        const fallbackUrl = bugData?.videoUrl || bugData?.video?.url || "";
        if (fallbackUrl) {
          if (fallbackUrl.startsWith("http")) {
            setVideoUrl(fallbackUrl);
          } else if (fallbackUrl) {
            setVideoLoading(true);
            try {
              const res = await postAttachmentKey({ key: fallbackUrl }).unwrap();
              const url = res?.data?.decryptedData?.url;
              if (url) setVideoUrl(url);
            } catch (err) {
              console.error("Error fetching fallback video URL:", err);
              // Try with S3 base URL as last resort
              setVideoUrl(`${S3_BASE_URL}/${fallbackUrl}`);
            } finally {
              setVideoLoading(false);
            }
          }
        }
        return;
      }

      // Case 1: already public URL (https)
      if (rawUrl.startsWith("https")) {
        setVideoUrl(rawUrl);
        return;
      }

      // Case 2: S3 key (dev/..., UI_AUTOMATION/..., etc.)
      setVideoLoading(true);
      try {
        const res = await postAttachmentKey({ key: rawUrl }).unwrap();
        const url = res?.data?.decryptedData?.url;
        if (url) {
          setVideoUrl(url);
        } else {
          // Fallback to S3 base URL
          setVideoUrl(`${S3_BASE_URL}/${rawUrl}`);
        }
      } catch (err) {
        console.error("Error fetching video URL:", err);
        // Fallback to S3 base URL as last resort
        setVideoUrl(`${S3_BASE_URL}/${rawUrl}`);
      } finally {
        setVideoLoading(false);
      }
    };

    if (bugData) {
      fetchVideoUrl();
    }
  }, [bugData, postAttachmentKey]);

  // Parse device details from JSON string
  const deviceDetails: DeviceDetails | null = useMemo(() => {
    if (bugData?.deviceDetails) {
      try {
        return JSON.parse(bugData.deviceDetails);
      } catch (e) {
        console.error("Error parsing deviceDetails:", e);
        return null;
      }
    }
    return null;
  }, [bugData]);

  // Fetch console and network logs from URLs (similar to TaskUpdateDrawer)
  useEffect(() => {
    const fetchLogs = async () => {
      if (!bugData?.logs) return;

      setLogsLoading(true);

      try {
        // Fetch console logs
        if (bugData.logs.console) {
          const consoleLogValue = bugData.logs.console;

          // Check if it's already an array
          if (Array.isArray(consoleLogValue)) {
            setConsoleLogs(consoleLogValue);
          } else if (typeof consoleLogValue === "string" && consoleLogValue.trim().length > 0) {
            // Try to parse as JSON first
            try {
              const parsed = JSON.parse(consoleLogValue);
              setConsoleLogs(Array.isArray(parsed) ? parsed : []);
            } catch {
              // Not JSON, treat as URL
              let fetchUrl = consoleLogValue;

              if (consoleLogValue.startsWith("https")) {
                // Already a full URL
                fetchUrl = consoleLogValue;
              } else {
                // S3 key - get signed URL
                try {
                  const res = await postAttachmentKey({ key: consoleLogValue }).unwrap();
                  const url = res?.data?.decryptedData?.url;
                  if (url) {
                    fetchUrl = url;
                  } else {
                    fetchUrl = `${S3_BASE_URL}/${consoleLogValue}`;
                  }
                } catch (err) {
                  console.error("Error getting console logs URL:", err);
                  fetchUrl = `${S3_BASE_URL}/${consoleLogValue}`;
                }
              }

              try {
                const response = await fetch(fetchUrl);
                if (response.ok) {
                  const data = await response.json();
                  setConsoleLogs(Array.isArray(data) ? data : []);
                }
              } catch (e) {
                console.error("Error fetching console logs:", e);
              }
            }
          }
        }

        // Fetch network logs
        if (bugData.logs.network) {
          const networkLogValue = bugData.logs.network;

          // Check if it's already an array
          if (Array.isArray(networkLogValue)) {
            setNetworkLogs(networkLogValue);
          } else if (typeof networkLogValue === "string" && networkLogValue.trim().length > 0) {
            // Try to parse as JSON first
            try {
              const parsed = JSON.parse(networkLogValue);
              setNetworkLogs(Array.isArray(parsed) ? parsed : []);
            } catch {
              // Not JSON, treat as URL
              let fetchUrl = networkLogValue;

              if (networkLogValue.startsWith("https")) {
                // Already a full URL
                fetchUrl = networkLogValue;
              } else {
                // S3 key - get signed URL
                try {
                  const res = await postAttachmentKey({ key: networkLogValue }).unwrap();
                  const url = res?.data?.decryptedData?.url;
                  if (url) {
                    fetchUrl = url;
                  } else {
                    fetchUrl = `${S3_BASE_URL}/${networkLogValue}`;
                  }
                } catch (err) {
                  console.error("Error getting network logs URL:", err);
                  fetchUrl = `${S3_BASE_URL}/${networkLogValue}`;
                }
              }

              try {
                const response = await fetch(fetchUrl);
                if (response.ok) {
                  const data = await response.json();
                  setNetworkLogs(Array.isArray(data) ? data : []);
                }
              } catch (e) {
                console.error("Error fetching network logs:", e);
              }
            }
          }
        }
      } finally {
        setLogsLoading(false);
      }
    };

    if (bugData) {
      fetchLogs();
    }
  }, [bugData, postAttachmentKey]);


  // Extract comments
  const allComments = commentsResponse?.data?.decryptedData?.items || commentsResponse?.data?.items || [];
  const videoComments = filterVideoComments(allComments);

  // Build system info from device details and bug data
  const systemInfo: SystemInfo = useMemo(() => {
    return {
      url: deviceDetails?.url || bugData?.pageUrl || bugData?.url,
      timestamp: bugData?.createdAt,
      os: deviceDetails?.operatingSystem || bugData?.os,
      browser: deviceDetails?.browser || bugData?.browser,
      windowSize: deviceDetails?.screen || bugData?.windowSize,
      screenshotDimensions: bugData?.screenshotDimensions,
    };
  }, [deviceDetails, bugData]);

  // State for fetched image URLs
  const [bugImages, setBugImages] = useState<Array<{ url: string; label?: string; type?: "cropped" | "fullscreen" | "attachment" }>>([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  const isLoading = isBugLoading || isCommentsLoading || logsLoading || videoLoading || imagesLoading;

  // Handler for adding public comments (no authentication required)
  const handleAddPublicComment = async (comment: string, timestamp: number, userName: string) => {
    if (!bugId) return;

    try {
      await createPublicComment({
        comment,
        itemId: bugId,
        module: "BUG_TRACKING",
        type: "PUBLIC",
        videoTimestamp: timestamp,
        isComment: true,
        userName,
        companyId: bugData?.company || bugData?.companyId || "",
      }).unwrap();

      refetchComments();
    } catch (error) {
      console.error("Error adding public comment:", error);
    }
  };

  // Fetch image URLs from attachments and screenshots
  useEffect(() => {
    const fetchImageUrls = async () => {
      if (!bugData) return;

      setImagesLoading(true);
      const images: Array<{ url: string; label?: string; type?: "cropped" | "fullscreen" | "attachment" }> = [];

      try {
        // Helper function to get signed URL
        const getSignedUrl = async (key: string): Promise<string> => {
          if (key.startsWith("http")) {
            return key;
          }
          try {
            const res = await postAttachmentKey({ key }).unwrap();
            const url = res?.data?.decryptedData?.url;
            return url || `${S3_BASE_URL}/${key}`;
          } catch (err) {
            console.error("Error fetching signed URL:", err);
            return `${S3_BASE_URL}/${key}`;
          }
        };

        // Add cropped screenshot
        if (bugData?.screenshot) {
          const screenshotUrl = await getSignedUrl(bugData.screenshot);
          images.push({
            url: screenshotUrl,
            label: "Cropped",
            type: "cropped",
          });
        }

        // Add full page screenshot
        if (bugData?.fullPageScreenshot) {
          const fullPageUrl = await getSignedUrl(bugData.fullPageScreenshot);
          images.push({
            url: fullPageUrl,
            label: "Full screen",
            type: "fullscreen",
          });
        }

        // Add attachments if they are images
        if (bugData?.attachments && Array.isArray(bugData.attachments)) {
          for (let i = 0; i < bugData.attachments.length; i++) {
            const attachment = bugData.attachments[i];
            const attachmentKey = attachment.url || attachment.key || attachment;

            if (typeof attachmentKey === "string") {
              // Check if it's an image (basic check by extension or type)
              const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachmentKey) ||
                attachmentKey.includes("image") ||
                attachment.type?.startsWith("image") ||
                attachment.mimeType?.startsWith("image");

              if (isImage) {
                const url = await getSignedUrl(attachmentKey);
                images.push({
                  url,
                  label: attachment.name || attachment.fileName || `Attachment ${i + 1}`,
                  type: "attachment",
                });
              }
            }
          }
        }

        setBugImages(images);
      } catch (err) {
        console.error("Error fetching image URLs:", err);
      } finally {
        setImagesLoading(false);
      }
    };

    if (bugData) {
      fetchImageUrls();
    }
  }, [bugData, postAttachmentKey]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PhantomLoader />
      </Box>
    );
  }

  // Error state
  if (bugError || !bugData) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fff",
        }}
      >
        <Typography variant="h5" color="grey.600" sx={{ mb: 2 }}>
          Bug Not Found
        </Typography>
        <Typography variant="body1" color="grey.500">
          The bug you're looking for doesn't exist or is not publicly accessible.
        </Typography>
      </Box>
    );
  }

  // No video case - show screenshot or message
  // if (!videoUrl) {
  //   const screenshotUrl = bugData?.screenshot || bugData?.fullPageScreenshot;

  //   return (
  //     <Box
  //       sx={{
  //         minHeight: "100vh",
  //         bgcolor: "#1a1a1a",
  //         color: "#fff",
  //         display: "flex",
  //         flexDirection: "column",
  //       }}
  //     >
  //       {/* Header */}
  //       <Box
  //         sx={{
  //           p: 2,
  //           borderBottom: "1px solid #333",
  //           display: "flex",
  //           alignItems: "center",
  //           gap: 2,
  //         }}
  //       >
  //         <Typography variant="h6" sx={{ fontWeight: 600 }}>
  //           {bugData?.summary || bugData?.title || bugData?.name || `Bug #${bugId}`}
  //         </Typography>
  //         {bugData?.bugKey && (
  //           <Typography variant="body2" color="grey.500">
  //             {bugData.bugKey}
  //           </Typography>
  //         )}
  //       </Box>

  //       {/* Content */}
  //       <Box sx={{ flex: 1, p: 3 }}>
  //         {/* Description */}
  //         {bugData?.description && (
  //           <Box sx={{ mb: 3 }}>
  //             <Typography variant="subtitle2" color="grey.400" sx={{ mb: 1 }}>
  //               Description
  //             </Typography>
  //             <Typography variant="body1">{bugData.description}</Typography>
  //           </Box>
  //         )}

  //         {/* Screenshot */}
  //         {screenshotUrl && (
  //           <Box sx={{ mb: 3 }}>
  //             <Typography variant="subtitle2" color="grey.400" sx={{ mb: 1 }}>
  //               Screenshot
  //             </Typography>
  //             <Box
  //               component="img"
  //               src={screenshotUrl}
  //               alt="Bug screenshot"
  //               sx={{
  //                 maxWidth: "100%",
  //                 maxHeight: "60vh",
  //                 objectFit: "contain",
  //                 borderRadius: 1,
  //                 border: "1px solid #333",
  //               }}
  //             />
  //           </Box>
  //         )}

  //         {/* System Info */}
  //         <Box sx={{ mb: 3 }}>
  //           <Typography variant="subtitle2" color="grey.400" sx={{ mb: 1 }}>
  //             System Information
  //           </Typography>
  //           <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
  //             {systemInfo.url && (
  //               <Typography variant="body2">
  //                 <strong>URL:</strong> {systemInfo.url}
  //               </Typography>
  //             )}
  //             {systemInfo.browser && (
  //               <Typography variant="body2">
  //                 <strong>Browser:</strong> {systemInfo.browser}
  //               </Typography>
  //             )}
  //             {systemInfo.os && (
  //               <Typography variant="body2">
  //                 <strong>OS:</strong> {systemInfo.os}
  //               </Typography>
  //             )}
  //             {systemInfo.windowSize && (
  //               <Typography variant="body2">
  //                 <strong>Window Size:</strong> {systemInfo.windowSize}
  //               </Typography>
  //             )}
  //           </Box>
  //         </Box>

  //         {/* Comments */}
  //         {allComments.length > 0 && (
  //           <Box>
  //             <Typography variant="subtitle2" color="grey.400" sx={{ mb: 1 }}>
  //               Comments ({allComments.length})
  //             </Typography>
  //             <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
  //               {allComments.map((comment: any) => (
  //                 <Box
  //                   key={comment._id}
  //                   sx={{
  //                     p: 2,
  //                     bgcolor: "#2a2a2a",
  //                     borderRadius: 1,
  //                   }}
  //                 >
  //                   <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
  //                     <Typography variant="body2" fontWeight={600}>
  //                       {comment.createdBy?.fullName || "Unknown User"}
  //                     </Typography>
  //                     <Typography variant="caption" color="grey.500">
  //                       {new Date(comment.createdAt).toLocaleString()}
  //                     </Typography>
  //                   </Box>
  //                   <Typography variant="body2">{comment.comment}</Typography>
  //                 </Box>
  //               ))}
  //             </Box>
  //           </Box>
  //         )}
  //       </Box>

  //       {/* Footer */}
  //       <Box
  //         sx={{
  //           p: 2,
  //           borderTop: "1px solid #333",
  //           textAlign: "center",
  //         }}
  //       >
  //         <Typography variant="caption" color="grey.600">
  //           Shared via Defect Tracker
  //         </Typography>
  //       </Box>
  //     </Box>
  //   );
  // }

  // Video case - use VideoPreviewPanel
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >

      {/* Video/Image Panel */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <VideoPreviewPanel
          videoUrl={videoUrl || ""}
          networkLogs={networkLogs}
          consoleLogs={consoleLogs}
          onClose={() => {
            window.history.back();
          }}
          isOpen={true}
          bugId={bugId}
          bugDetailData={bugData}
          systemInfo={systemInfo}
          isPublic={true}
          publicComments={videoComments}
          images={bugImages}
          onAddPublicComment={handleAddPublicComment}
        />
      </Box>
    </Box>
  );
};

// Wrap with Provider for standalone usage
const PublicBugDetail: React.FC = () => {
  return (
    <Provider store={store}>
      <PublicBugDetailContent />
    </Provider>
  );
};

export default PublicBugDetail;
